import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CurrencyInr } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  {
    value: 1, label: 'Mode 1 · Inquiry-only',
    desc: 'All vendor + venue lines are free. Vendor confirms availability → phone revealed. No payment anywhere.',
  },
  {
    value: 2, label: 'Mode 2 · Venue Advance (Non-Wedding)',
    desc: 'Non-wedding events charge an upfront advance for VENUE lines only. Vendor lines remain free inquiry. Wedding events stay fully free.',
  },
  {
    value: 3, label: 'Mode 3 · Full Advance (Non-Wedding)',
    desc: 'Non-wedding events charge an upfront advance for both VENUE and VENDOR lines. Wedding events stay fully free.',
  },
];

/**
 * Admin tile to switch the global vendor / venue payment mode and set the
 * flat advance per qualifying line. Sits below the Pro Response Window panel
 * in Admin → KYC tab.
 */
const AdminVendorPaymentModePanel = () => {
  const [mode, setMode] = useState(1);
  const [fee, setFee] = useState(500);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/vendor-payment-mode`, { withCredentials: true });
        setMode(Number(data?.mode) || 1);
        setFee(Number(data?.fee) || 500);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load setting');
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/vendor-payment-mode`,
        { mode: Number(mode), advance_fee_inr: Number(fee) }, { withCredentials: true });
      setMode(Number(data?.mode));
      setFee(Number(data?.fee));
      toast.success(`Mode ${data.mode} active · advance ₹${data.fee} per line`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <Card className="bg-white border-[#E8E1D5] mb-4" data-testid="admin-vendor-payment-mode">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <CurrencyInr size={20} className="text-brand-primary" /> Vendor / Venue Payment Mode
        </CardTitle>
        <CardDescription>
          Controls whether customers pay an upfront advance to book vendor / venue lines. Wedding events (Marriage, Engagement, Reception) are always free under every mode — they go through the separate pro-confirmation flow.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-5 space-y-4">
        <div className="space-y-2">
          {MODES.map(m => (
            <label key={m.value}
              className={`block border-2 rounded p-3 cursor-pointer transition-colors ${Number(mode) === m.value ? 'border-brand-primary bg-brand-accent/5' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
              data-testid={`vp-mode-${m.value}`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="vp_mode" value={m.value}
                  checked={Number(mode) === m.value}
                  onChange={() => setMode(m.value)}
                  className="h-4 w-4 accent-brand-primary" />
                <span className="font-bold text-sm">{m.label}</span>
              </div>
              <p className="text-xs text-text-secondary mt-1 ml-6">{m.desc}</p>
            </label>
          ))}
        </div>

        <div className="flex items-end gap-3 flex-wrap pt-2 border-t border-[#E8E1D5]">
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs uppercase tracking-wider font-bold text-text-secondary block mb-1">
              Advance per qualifying line (₹)
            </label>
            <Input type="number" min={1} value={fee}
              onChange={(e) => setFee(e.target.value)}
              className="rounded-none" data-testid="vp-fee-input" />
            <p className="text-[10px] text-text-secondary mt-1">
              Charged once per qualifying line (e.g., ₹{fee} × 2 venues = ₹{fee * 2}).
            </p>
          </div>
          <Button onClick={save} disabled={saving}
            className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
            data-testid="vp-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminVendorPaymentModePanel;
